class DynamicFormHandler {
    constructor(translator) {
        if (!translator) throw new Error('DynamicFormHandler: translator is required');
        this.translator = translator;
    }

    _setSingleOption(select, key) {
        const option = document.createElement('option');
        option.value = '';
        option.textContent = this.translator.trans(key);
        select.replaceChildren(option);
    }

    /**
     * Initializes dynamic behavior on the given form element.
     * @param {HTMLFormElement} formElement - The <form> to attach behaviors to.
     */
    init(formElement) {
        this.form = formElement;

        // 1. Find all elements with data-dh-action (could be comma-separated)
        const dynamicElements = this.form.querySelectorAll("[data-dh-action]");

        dynamicElements.forEach((el) => {
            const actionAttr = el.getAttribute("data-dh-action");
            const actions = actionAttr
                .split(",")
                .map((a) => a.trim())
                .filter((a) => a);

            // Attach event listeners for each action
            actions.forEach((action) => {
                switch (action) {
                    case "display-block":
                        el.addEventListener("change", (e) => this._handleDisplay(e, 'block'));
                        break;
                    case "display-flex":
                        el.addEventListener("change", (e) => this._handleDisplay(e, 'flex'));
                        break;
                    case "display-none":
                        el.addEventListener("change", (e) => this._handleDisplay(e, 'none'));
                        break;
                    case "toggle-flex":
                        el.addEventListener("change", (e) => this._handleToggleDisplay(e, 'flex'));
                        break;
                    case "toggle-block":
                        el.addEventListener("change", (e) => this._handleToggleDisplay(e, 'block'));
                        break;
                    case "toggle-enable":
                        el.addEventListener("change", (e) => this._handleToggleEnable(e));
                        break;
                    case "fetch":
                        el.addEventListener("change", (e) => this._handleFetch(e, 'fetch'));
                        break;
                    case "fetch-other":
                        el.addEventListener("change", (e) => this._handleFetch(e, 'fetch-other'));
                        break;
                    case "require":
                        el.addEventListener("change", (e) => this._handleRequire(e));
                        break;
                    case "toggle-require":
                        // NEW action for toggling required presence on two sets of elements
                        el.addEventListener("change", (e) => this._handleToggleRequire(e));
                        break;
                    default:
                        console.warn(`Unknown dynamic action: ${action}`);
                        break;
                }
            });
        });

        // 2. Run actions immediately if data-dh-init is present
        this._runInitialActions();
    }

    /**
     * If an element has data-dh-init, call its actions on initialization
     * (rather than waiting for the user to change something).
     */
    _runInitialActions() {
        // 1. Find all elements with data-dh-action (could be comma-separated)
        const dynamicElements = this.form.querySelectorAll("[data-dh-action]");

        dynamicElements.forEach((el) => {
            if (!el.hasAttribute("data-dh-init")) return;

            const actionAttr = el.getAttribute("data-dh-action");
            if (!actionAttr) return;

            const actions = actionAttr
                .split(",")
                .map((a) => a.trim())
                .filter((a) => a);

            // Manually invoke each action’s handler
            actions.forEach((action) => {
                switch (action) {
                    case "display-block":
                        this._handleDisplay({ currentTarget: el }, 'block');
                        break;
                    case "display-none":
                        this._handleDisplay({ currentTarget: el }, 'none');
                        break;
                    case "toggle-flex":
                        this._handleToggleDisplay({ currentTarget: el }, 'flex');
                        break;
                    case "toggle-block":
                        this._handleToggleDisplay({ currentTarget: el }, 'block');
                        break;
                    case "toggle-enable":
                        this._handleToggleEnable({ currentTarget: el });
                        break;
                    case "fetch":
                        this._handleFetch({ currentTarget: el });
                        break;
                    case "fetch-other":
                        this._handleFetch({ currentTarget: el });
                        break;
                    case "require":
                        this._handleRequire({ currentTarget: el });
                        break;
                    case "toggle-require":
                        this._handleToggleRequire({ currentTarget: el });
                        break;
                    default:
                        // no-op
                        break;
                }
            });
        });
    }

    /**
     * Retrieves the target(s) for a given action, checking both
     * data-dh-target-ACTION and fallback data-dh-target (if needed).
     */
    _getTargetsForAction(triggerElement, action) {
        const actionSpecificAttr = `data-dh-target-${action}`;
        let targetSelectors = triggerElement.getAttribute(actionSpecificAttr);

        if (!targetSelectors) {
            targetSelectors = triggerElement.getAttribute("data-dh-target");
        }

        if (!targetSelectors) {
            return [];
        }

        const nodeList = document.querySelectorAll(targetSelectors);
        const targets = Array.from(nodeList);

        if (targets.length === 0) {
            console.warn(`No elements found for selector(s): '${targetSelectors}'`);
        }
        return targets;
    }

    /**
     * Checks if the action's condition is satisfied, including "not:" negation.
     * Priority for condition attributes:
     *   1) data-dh-condition-ACTION
     *   2) data-dh-condition
     *
     * Supported forms:
     *   - "checked" / "unchecked" for checkboxes
     *   - "ANY" => non-empty value (for selects/inputs)
     *   - literal string => must match element.value
     *   - "not:XYZ" => invert the logic of XYZ
     */
    _meetsCondition(triggerElement, action) {
        const actionSpecificAttr = `data-dh-condition-${action}`;
        let condition = triggerElement.getAttribute(actionSpecificAttr);

        if (!condition) {
            condition = triggerElement.getAttribute("data-dh-condition");
        }

        // No condition => always pass
        if (!condition) return true;

        // Check if the condition is a "not:" form. e.g., "not:checked", "not:users"
        let isNot = false;
        let subCondition = condition;

        const notPrefix = "not:";
        if (condition.startsWith(notPrefix)) {
            isNot = true;
            subCondition = condition.slice(notPrefix.length).trim(); // remove "not:"
        }

        // Evaluate the sub-condition normally
        const passes = this._evaluateCondition(triggerElement, subCondition);

        // If isNot => invert the result
        return isNot ? !passes : passes;
    }

    /**
     * Evaluate the "base" condition (without "not:").
     * For checkboxes (and optionally radios), we handle them as a group if they share a name.
     */
    _evaluateCondition(triggerElement, condition) {
        const tag = triggerElement.tagName.toLowerCase();
        const type = (triggerElement.getAttribute("type") || "").toLowerCase();

        // 1) CHECKBOX logic
        if (type === "checkbox") {
            // Grab the name attribute to see if it's part of a group
            const name = triggerElement.name ? triggerElement.name.trim() : "";

            // If there is a name, treat all checkboxes with that name as a group
            if (name) {
                // Find all checkboxes in the same form with the same name
                const checkboxes = Array.from(
                    this.form.querySelectorAll(`input[type="checkbox"][name="${name}"]`)
                );

                switch (condition) {
                    // "checked" => at least one checkbox in the group is checked
                    case "checked":
                        return checkboxes.some(cb => cb.checked);

                    // "unchecked" => none of the checkboxes in the group are checked
                    case "unchecked":
                        return checkboxes.every(cb => !cb.checked);

                    // "ANY" => at least one checkbox in the group is checked
                    case "ANY":
                        return checkboxes.some(cb => cb.checked);

                    default:
                        // If the condition is a literal string, 
                        // check if any checked box's value matches that string
                        return checkboxes.some(cb => cb.checked && cb.value === condition);
                }
            } else {
                // If there's no name (i.e., a single standalone checkbox),
                // fall back to the original single-checkbox logic
                if (condition === "checked")   return triggerElement.checked === true;
                if (condition === "unchecked") return triggerElement.checked === false;
                if (condition === "ANY")       return !!triggerElement.checked;

                // Otherwise, literal match against .value
                return triggerElement.value === condition;
            }
        }

        // 2) RADIO logic (optional). Similar grouping by name:
        if (type === "radio") {
            const name = triggerElement.name ? triggerElement.name.trim() : "";
            if (name) {
                const radios = Array.from(
                    this.form.querySelectorAll(`input[type="radio"][name="${name}"]`)
                );

                switch (condition) {
                    case "checked":
                        // "checked" => at least one radio in group is checked
                        return radios.some(rb => rb.checked);

                    case "unchecked":
                        // "unchecked" => no radio in group is checked
                        return radios.every(rb => !rb.checked);

                    case "ANY":
                        // "ANY" => at least one is checked
                        return radios.some(rb => rb.checked);

                    default:
                        // Literal match: does any checked radio have value == condition?
                        return radios.some(rb => rb.checked && rb.value === condition);
                }
            } else {
                // Single, standalone radio fallback
                if (condition === "checked")   return triggerElement.checked === true;
                if (condition === "unchecked") return triggerElement.checked === false;
                if (condition === "ANY")       return !!triggerElement.checked;
                return triggerElement.value === condition;
            }
        }

        // 3) ALL OTHER ELEMENTS (text, select, etc.)
        // "ANY" => non-empty value
        if (condition === "ANY") {
            return !!triggerElement.value;
        }

        // Otherwise, must match exactly
        return triggerElement.value === condition;
    }

    /**
     * SHOW/HIDE action: sets display = "block" on all targets if condition is met.
     */
    _handleDisplay(event, display) {
        const triggerElement = event.currentTarget;
        const action = `display-${display}`;
        if (!this._meetsCondition(triggerElement, action)) {
            return;
        }

        const targets = this._getTargetsForAction(triggerElement, action);
        targets.forEach((target) => {
            if (['block', 'none', 'flex'].indexOf(display) > 0) {
                target.style.display = display;
            }
        });
    }

    /**
     * FETCH action: fetch data from a remote URL if condition is met.
     */
    async _handleFetch(event, action) {
        const triggerElement = event.currentTarget;
        if (!this._meetsCondition(triggerElement, action)) {
            return;
        }

        let url = triggerElement.getAttribute(`data-dh-${action}-url`);
        if (!url) return; // No URL means no fetch

        url = url
            .replace("%7Bvalue%7D", encodeURIComponent(triggerElement.value))
            .replace("{value}", encodeURIComponent(triggerElement.value))
        ;

        const targets = this._getTargetsForAction(triggerElement, action);

        // Possibly handle empty value
        if (!triggerElement.value) {
            targets.forEach((targetSelect) => {
                if (targetSelect.tagName.toLowerCase() === "select") {
                    this._setSingleOption(targetSelect, 'dynamicForm.noData');
                }
            });
            return;
        }

        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Network response was not ok: ${response.statusText}`);
            }
            const jsonData = await response.json();

            targets.forEach((targetSelect) => {
                if (targetSelect.tagName.toLowerCase() !== "select") {
                    console.warn("Target for fetch is not a <select>:", targetSelect);
                    return;
                }

                const previousValue = targetSelect.value;

                // Clear existing options
                targetSelect.innerHTML = "";

                // Example: we assume array of objects with 'id' and 'label'
                if (Array.isArray(jsonData)) {
                    jsonData.forEach((item) => {
                        const opt = document.createElement("option");
                        opt.value = item.id;
                        opt.textContent = item.label;
                        targetSelect.appendChild(opt);
                    });

                    targetSelect.value = previousValue; // Set the value.
                } else {
                    this._setSingleOption(targetSelect, 'dynamicForm.invalidData');
                }
            });
        } catch (error) {
            console.error("Failed to fetch data:", error);
            targets.forEach((targetSelect) => {
                if (targetSelect.tagName.toLowerCase() === "select") {
                    this._setSingleOption(targetSelect, 'dynamicForm.fetchError');
                }
            });
        }
    }

    /**
     * ASTERISK action: prefix "*" to label text if condition is met.
     */
    _handleRequire(event) {
        const triggerElement = event.currentTarget;
        if (!this._meetsCondition(triggerElement, "require")) {
            return;
        }

        const targets = this._getTargetsForAction(triggerElement, "require");
        this._applyRequireToTargets(targets);
    }

    /**
     * TOGGLE-BETWEEN action: 
     *  - If condition is met => show 'toggle-flex-block' targets, hide 'toggle-flex-none' targets.
     *  - Otherwise => hide 'toggle-flex-block' and show 'toggle-flex-none'.
     */
    _handleToggleDisplay(event, display) {
        const triggerElement = event.currentTarget;
        const conditionMet = this._meetsCondition(triggerElement, `toggle-${display}`);

        // Targets to show if condition is met
        const showTargets = this._getTargetsForAction(triggerElement, `toggle-${display}-show`);
        // Targets to hide if condition is met
        const hideTargets = this._getTargetsForAction(triggerElement, `toggle-${display}-hide`);

        if (conditionMet) {
            // Show one group, hide the other
            showTargets.forEach((el) => (el.style.display = display));
            hideTargets.forEach((el) => (el.style.display = "none"));
        } else {
            // Reverse
            showTargets.forEach((el) => (el.style.display = "none"));
            hideTargets.forEach((el) => (el.style.display = display));
        }
    }

    /**
     * TOGGLE-ENABLE action:
     * - enable targets when the condition is met;
     * - disable and reset targets when the condition is not met.
     *
     * Resetting is important for form controls because disabled fields are not
     * submitted by the browser. It also prevents a previously selected value
     * from becoming active again unexpectedly when the trigger changes.
     */
    _handleToggleEnable(event) {
        const triggerElement = event.currentTarget;
        const conditionMet = this._meetsCondition(triggerElement, "toggle-enable");
        const targets = this._getTargetsForAction(triggerElement, "toggle-enable");

        targets.forEach((target) => {
            target.disabled = !conditionMet;

            if (conditionMet) {
                return;
            }

            const tag = target.tagName.toLowerCase();
            const type = (target.getAttribute?.("type") || "").toLowerCase();

            if (tag === "select") {
                target.selectedIndex = 0;
                return;
            }

            if (type === "checkbox" || type === "radio") {
                target.checked = !!target.defaultChecked;
                return;
            }

            if (tag === "input" || tag === "textarea") {
                target.value = target.defaultValue ?? "";
            }
        });
    }

    /**
     * TOGGLE-REQUIRED action:
     * - If condition is met => add required class to 'toggle-require-show', remove required class from 'toggle-require-hide'
     * - Otherwise => remove required class from 'toggle-require-show', add required class to 'toggle-require-hide'
     */
    _handleToggleRequire(event) {
        const triggerElement = event.currentTarget;
        const conditionMet = this._meetsCondition(triggerElement, "toggle-require");

        // The sets of targets
        const addTargets = this._getTargetsForAction(triggerElement, "toggle-require-show");
        const removeTargets = this._getTargetsForAction(triggerElement, "toggle-require-hide");

        if (conditionMet) {
            // Condition is true => add required to addTargets, remove from removeTargets
            this._applyRequireToTargets(addTargets);
            this._removeRequireFromTargets(removeTargets);
        } else {
            // Condition is false => remove required from addTargets, add to removeTargets
            this._removeRequireFromTargets(addTargets);
            this._applyRequireToTargets(removeTargets);
        }
    }

    /**
     * Helper to add an required to the label class referencing each target
     */
    _applyRequireToTargets(targets) {
        targets.forEach((control) => {
            if (!control.id) {
                console.warn("Required action: target has no 'id'.", control);
                return;
            }

            const labelSelector = `label[for="${control.id}"]`;
            const labels = this.form.querySelectorAll(labelSelector);

            if (labels.length === 0) {
                console.warn(`No label found with for="${control.id}"`);
            }

            labels.forEach((label) => {
                if (!label.classList.contains('required')) {
                    label.classList.add('required');
                }
            });
        });
    }

    /**
     * Helper to remove required class from the label referencing each target
     */
    _removeRequireFromTargets(targets) {
        targets.forEach((control) => {
            if (!control.id) {
                console.warn("Required action: target has no 'id'.", control);
                return;
            }

            const labelSelector = `label[for="${control.id}"]`;
            const labels = this.form.querySelectorAll(labelSelector);

            if (labels.length === 0) {
                console.warn(`No label found with for="${control.id}"`);
            }

            labels.forEach((label) => {
                if (label.classList.contains('required')) {
                    label.classList.remove('required');
                }
            });
        });
    }
}

export default DynamicFormHandler;

